import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { ChevronDown, ChevronRight, ExternalLink, Check, X, CreditCard, FileText, Tag, Clock, Loader2, Settings2, AlertTriangle } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../ui/collapsible';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Input } from '../ui/input';
import { Separator } from '../ui/separator';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import api from '../../lib/api';

const GHL_CONTACT_BASE = 'https://app.leadseverywhere.net/v2/location/2llwOnTMGXnzyMKidM7j/contacts/detail';
const STRIPE_SEARCH_BASE = 'https://dashboard.stripe.com/search?query=';

function formatDate(dateStr) {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  return `${d.getMonth() + 1}/${d.getDate()}/${String(d.getFullYear()).slice(-2)}`;
}

function formatCurrency(val) {
  const num = parseFloat(val) || 0;
  return `$${num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export function PendingCommissions({ staffList = [] }) {
  const [items, setItems] = useState([]);
  const [highlightTags, setHighlightTags] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(true);
  const [processing, setProcessing] = useState(null);
  const [tagsModalOpen, setTagsModalOpen] = useState(false);
  const [newTagKeyword, setNewTagKeyword] = useState('');
  const [newTagColor, setNewTagColor] = useState('#f59e0b');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get('/api/payroll/pending-commissions');
      setItems(res.data.data || []);
      setHighlightTags(res.data.highlight_tags || []);
    } catch (err) {
      console.error('[PendingCommissions] Load failed:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleAction = async (id, status) => {
    setProcessing(id);
    try {
      await api.patch(`/api/payroll/pending-commissions/${id}`, { status });
      setItems(prev => prev.filter(i => i.id !== id));
      toast.success(status === 'processed' ? 'Marked as processed' : 'Dismissed');
    } catch (err) {
      toast.error('Failed to update');
    } finally {
      setProcessing(null);
    }
  };

  const getStaffName = (email) => {
    if (!email) return null;
    const match = staffList.find(s => s.email?.toLowerCase() === email.toLowerCase());
    return match?.name || null;
  };

  const renderTag = (tagText) => {
    const text = tagText.trim();
    if (!text) return null;
    const matchingRule = highlightTags.find(ht =>
      text.toLowerCase().includes(ht.tag_keyword.toLowerCase())
    );
    if (matchingRule) {
      return (
        <Badge
          key={text}
          className="text-[10px] px-1.5 py-0 font-medium mr-1 mb-0.5"
          style={{ backgroundColor: matchingRule.color, color: '#fff', borderColor: matchingRule.color }}
          data-testid={`highlight-tag-${text}`}
        >
          {text}
        </Badge>
      );
    }
    return (
      <Badge key={text} variant="secondary" className="text-[10px] px-1.5 py-0 font-medium mr-1 mb-0.5" data-testid={`tag-${text}`}>
        {text}
      </Badge>
    );
  };

  const getStripeLink = (item) => {
    try {
      const lineItems = item.line_items || [];
      if (lineItems.length > 0) {
        const invoiceId = lineItems[0]?.meta?.invoice_id;
        if (invoiceId) return `${STRIPE_SEARCH_BASE}${invoiceId}`;
      }
    } catch {}
    return null;
  };

  const handleAddTag = async () => {
    if (!newTagKeyword.trim()) return;
    try {
      await api.post('/api/settings/highlight-tags', {
        tag_keyword: newTagKeyword.trim(),
        color: newTagColor,
      });
      setNewTagKeyword('');
      setNewTagColor('#f59e0b');
      load();
      toast.success('Tag added');
    } catch {
      toast.error('Failed to add tag');
    }
  };

  const handleDeleteTag = async (tagId) => {
    try {
      await api.delete(`/api/settings/highlight-tags/${tagId}`);
      load();
      toast.success('Tag removed');
    } catch {
      toast.error('Failed to remove tag');
    }
  };

  if (loading && items.length === 0) return null;

  return (
    <>
      <Collapsible open={open} onOpenChange={setOpen} data-testid="pending-commissions-section">
        <Card className="border-amber-200/50 dark:border-amber-800/30 bg-amber-50/30 dark:bg-amber-950/10">
          <CollapsibleTrigger asChild>
            <div className="flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-amber-50/50 dark:hover:bg-amber-950/20 transition-colors duration-150" data-testid="pending-commissions-toggle">
              <div className="flex items-center gap-2">
                {open ? <ChevronDown className="h-4 w-4 text-amber-600" /> : <ChevronRight className="h-4 w-4 text-amber-600" />}
                <Clock className="h-4 w-4 text-amber-600" />
                <span className="font-semibold text-sm">Pending Commissions</span>
                <Badge variant="outline" className="text-amber-700 border-amber-300 bg-amber-100/50 dark:text-amber-400 dark:border-amber-700 dark:bg-amber-900/30 text-xs" data-testid="pending-count-badge">
                  {loading ? '...' : `${items.length} pending`}
                </Badge>
              </div>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 px-2 text-xs"
                onClick={(e) => { e.stopPropagation(); setTagsModalOpen(true); }}
                data-testid="highlight-tags-settings-btn"
              >
                <Settings2 className="h-3.5 w-3.5 mr-1" />
                Tags
              </Button>
            </div>
          </CollapsibleTrigger>

          <CollapsibleContent>
            <CardContent className="px-4 pt-0 pb-3">
              {loading ? (
                <div className="flex items-center justify-center py-6" data-testid="pending-loading">
                  <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                </div>
              ) : (
                <div className="rounded-md border overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="text-xs">
                        <TableHead className="w-[70px]">Date</TableHead>
                        <TableHead className="w-[140px]">Customer</TableHead>
                        <TableHead className="w-[120px]">Company</TableHead>
                        <TableHead className="w-[80px]">Type</TableHead>
                        <TableHead className="min-w-[180px]">Line Items</TableHead>
                        <TableHead className="w-[80px] text-right">Total</TableHead>
                        <TableHead className="w-[130px]">Sales Rep</TableHead>
                        <TableHead className="min-w-[140px]">Tags</TableHead>
                        <TableHead className="w-[80px]">Invoice</TableHead>
                        <TableHead className="w-[50px]">Stripe</TableHead>
                        <TableHead className="w-[100px] text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {items.map(item => {
                        const stripeLink = getStripeLink(item);
                        const repName = getStaffName(item.salesrep_email);
                        const tags = (item.tags || '').split(',').filter(Boolean);
                        const isProcessing = processing === item.id;

                        return (
                          <TableRow key={item.id} className="text-xs" data-testid={`pending-row-${item.id}`}>
                            <TableCell className="py-2 text-muted-foreground whitespace-nowrap">
                              {formatDate(item.payment_date)}
                            </TableCell>
                            <TableCell className="py-2">
                              {item.contact_id ? (
                                <a
                                  href={`${GHL_CONTACT_BASE}/${item.contact_id}?view=payments`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-blue-600 dark:text-blue-400 hover:underline flex items-center gap-1"
                                  data-testid={`customer-link-${item.id}`}
                                >
                                  {item.customer_name || 'Unknown'}
                                  <ExternalLink className="h-3 w-3 flex-shrink-0" />
                                </a>
                              ) : (
                                <span>{item.customer_name || 'Unknown'}</span>
                              )}
                            </TableCell>
                            <TableCell className="py-2 text-muted-foreground">
                              {item.company_name || '—'}
                            </TableCell>
                            <TableCell className="py-2">
                              <Badge variant="outline" className="text-[10px] px-1.5 py-0 font-medium" data-testid={`sale-type-${item.id}`}>
                                {item.sale_type || '—'}
                              </Badge>
                            </TableCell>
                            <TableCell className="py-2">
                              {(item.line_items || []).length > 0 ? (
                                <div className="space-y-0.5">
                                  {item.line_items.map((li, idx) => {
                                    const ct = li.commission_type;
                                    const typeBadge = ct === 'one_time'
                                      ? <Badge className="text-[9px] px-1 py-0 bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400 border-0 mr-1">One-Time</Badge>
                                      : ct === 'monthly'
                                      ? <Badge className="text-[9px] px-1 py-0 bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 border-0 mr-1">Monthly</Badge>
                                      : ct === 'review'
                                      ? <Badge className="text-[9px] px-1 py-0 bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 border-0 mr-1">Review</Badge>
                                      : null;
                                    return (
                                      <div key={idx} className="text-[11px] flex items-center gap-0.5">
                                        {typeBadge}
                                        <span className="text-muted-foreground truncate max-w-[120px]">{li.title || li.name || 'Item'}:</span>{' '}
                                        <span className="font-medium">{formatCurrency(li.price || li.amount || 0)}</span>
                                      </div>
                                    );
                                  })}
                                </div>
                              ) : (
                                <span className="text-muted-foreground">—</span>
                              )}
                            </TableCell>
                            <TableCell className="py-2 text-right whitespace-nowrap">
                              <div className="space-y-0.5">
                                <div className="font-semibold">{formatCurrency(item.total_amount)}</div>
                                {(item.one_time_total != null || item.monthly_total != null) && (
                                  <div className="text-[10px] text-muted-foreground">
                                    {item.one_time_total ? <span className="text-emerald-600 dark:text-emerald-400">OT: {formatCurrency(item.one_time_total)}</span> : null}
                                    {item.one_time_total && item.monthly_total ? <span> · </span> : null}
                                    {item.monthly_total ? <span className="text-blue-600 dark:text-blue-400">Mo: {formatCurrency(item.monthly_total)}</span> : null}
                                  </div>
                                )}
                              </div>
                              {item.needs_review && (
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <AlertTriangle className="h-3.5 w-3.5 text-amber-500 inline-block ml-1" data-testid={`review-warning-${item.id}`} />
                                    </TooltipTrigger>
                                    <TooltipContent side="left" className="max-w-xs text-xs">
                                      <p className="font-semibold mb-1">Needs Review</p>
                                      {(item.line_items || []).filter(li => li.commission_type === 'review').map((li, i) => (
                                        <p key={i}>{li.title}: {li.classification_reason}</p>
                                      ))}
                                    </TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              )}
                            </TableCell>
                            <TableCell className="py-2">
                              <div className="flex flex-col gap-0.5">
                                {repName ? (
                                  <span className="font-medium">{repName}</span>
                                ) : (
                                  <span className="text-muted-foreground">{item.salesrep_email || '—'}</span>
                                )}
                                {item.salesrep_matched === false && item.salesrep_email && (
                                  <Badge className="text-[9px] px-1 py-0 bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400 border-0 w-fit" data-testid={`unmatched-rep-${item.id}`}>
                                    Unmatched Rep
                                  </Badge>
                                )}
                              </div>
                            </TableCell>
                            <TableCell className="py-2">
                              <div className="flex flex-wrap">
                                {tags.length > 0 ? tags.map(t => renderTag(t)) : <span className="text-muted-foreground">—</span>}
                              </div>
                            </TableCell>
                            <TableCell className="py-2">
                              {item.invoice_url ? (
                                <a
                                  href={item.invoice_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-blue-600 dark:text-blue-400 hover:underline flex items-center gap-1"
                                  data-testid={`invoice-link-${item.id}`}
                                >
                                  <FileText className="h-3 w-3" />
                                  {item.invoice_number || 'View'}
                                </a>
                              ) : (
                                <span className="text-muted-foreground">{item.invoice_number || '—'}</span>
                              )}
                            </TableCell>
                            <TableCell className="py-2">
                              {stripeLink ? (
                                <a
                                  href={stripeLink}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-purple-600 dark:text-purple-400 hover:underline"
                                  data-testid={`stripe-link-${item.id}`}
                                >
                                  <CreditCard className="h-3.5 w-3.5" />
                                </a>
                              ) : (
                                <span className="text-muted-foreground">—</span>
                              )}
                            </TableCell>
                            <TableCell className="py-2 text-right">
                              <div className="flex items-center justify-end gap-1">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-6 w-6 p-0 text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 dark:hover:bg-emerald-950/30"
                                  disabled={isProcessing}
                                  onClick={() => handleAction(item.id, 'processed')}
                                  data-testid={`mark-processed-${item.id}`}
                                  title="Mark Processed"
                                >
                                  {isProcessing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-6 w-6 p-0 text-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-950/30"
                                  disabled={isProcessing}
                                  onClick={() => handleAction(item.id, 'dismissed')}
                                  data-testid={`dismiss-${item.id}`}
                                  title="Dismiss"
                                >
                                  <X className="h-3.5 w-3.5" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </CollapsibleContent>
        </Card>
      </Collapsible>

      {/* Highlight Tags Settings Modal */}
      <Dialog open={tagsModalOpen} onOpenChange={setTagsModalOpen}>
        <DialogContent className="sm:max-w-md" data-testid="highlight-tags-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <Tag className="h-4 w-4" />
              Highlight Tag Keywords
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-muted-foreground">
              Tags containing these keywords will be highlighted with the configured color.
            </p>
            <div className="space-y-2">
              {highlightTags.map(ht => (
                <div key={ht.id} className="flex items-center justify-between gap-2 p-2 rounded border" data-testid={`tag-rule-${ht.id}`}>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded" style={{ backgroundColor: ht.color }} />
                    <span className="text-sm font-medium">{ht.tag_keyword}</span>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 w-6 p-0 text-red-500 hover:text-red-600"
                    onClick={() => handleDeleteTag(ht.id)}
                    data-testid={`delete-tag-${ht.id}`}
                  >
                    <X className="h-3.5 w-3.5" />
                  </Button>
                </div>
              ))}
              {highlightTags.length === 0 && (
                <p className="text-xs text-muted-foreground text-center py-2">No highlight rules configured.</p>
              )}
            </div>
            <Separator />
            <div className="flex items-center gap-2">
              <Input
                placeholder="Keyword (e.g. rachel)"
                value={newTagKeyword}
                onChange={e => setNewTagKeyword(e.target.value)}
                className="h-8 text-sm flex-1"
                data-testid="new-tag-keyword-input"
              />
              <input
                type="color"
                value={newTagColor}
                onChange={e => setNewTagColor(e.target.value)}
                className="w-8 h-8 rounded border cursor-pointer"
                data-testid="new-tag-color-input"
              />
              <Button
                size="sm"
                className="h-8"
                onClick={handleAddTag}
                disabled={!newTagKeyword.trim()}
                data-testid="add-tag-btn"
              >
                Add
              </Button>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setTagsModalOpen(false)} data-testid="close-tags-modal-btn">
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
